import React, { useCallback, useEffect, useRef } from 'react';
import './PodiumFireworks.css';

const FIREWORK_COLORS = ['#FFD700', '#FF6B6B', '#4ECDC4', '#A78BFA', '#FF9F1C', '#38BDF8', '#F472B6', '#84CC16'];
const MAX_PARTICLES = 600;
const SHOW_DURATION_MS = 3000;
const BURST_INTERVAL_MS = 350;

const createBurst = (x, y) => {
  const count = 32 + Math.floor(Math.random() * 12);
  const particles = [];
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.3;
    const speed = 2 + Math.random() * 3.4;
    particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      alpha: 1,
      size: 2 + Math.random() * 2,
      color: FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)],
      gravity: 0.045,
      decay: 0.012 + Math.random() * 0.012
    });
  }
  return particles;
};

// Праздничный салют на canvas: автоматически проигрывается 3 секунды при появлении нового подиума
const PodiumFireworks = ({ triggerKey, children }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);
  const sizeRef = useRef({ width: 0, height: 0 });

  const ensureLoop = useCallback(() => {
    if (rafRef.current) return;

    const loop = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        rafRef.current = null;
        return;
      }

      const ctx = canvas.getContext('2d');
      const { width, height } = sizeRef.current;
      ctx.clearRect(0, 0, width, height);

      const particles = particlesRef.current;
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.alpha -= p.decay;

        if (p.alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }

        ctx.globalAlpha = Math.max(p.alpha, 0);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      if (particles.length > 0) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(loop);
  }, []);

  const fireBurst = useCallback((xRatio, yRatio) => {
    const { width, height } = sizeRef.current;
    if (!width || !height) return;

    const particles = particlesRef.current;
    const burst = createBurst(xRatio * width, yRatio * height);

    const overflow = particles.length + burst.length - MAX_PARTICLES;
    if (overflow > 0) {
      particles.splice(0, overflow);
    }
    particles.push(...burst);

    ensureLoop();
  }, [ensureLoop]);

  useEffect(() => {
    const updateSize = () => {
      const el = containerRef.current;
      const canvas = canvasRef.current;
      if (!el || !canvas) return;

      const rect = el.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      sizeRef.current = { width: rect.width, height: rect.height };
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    if (!triggerKey) return;

    const timers = [];
    for (let elapsed = 0; elapsed < SHOW_DURATION_MS; elapsed += BURST_INTERVAL_MS) {
      timers.push(setTimeout(() => {
        fireBurst(0.15 + Math.random() * 0.7, 0.15 + Math.random() * 0.4);
      }, elapsed));
    }

    return () => timers.forEach(clearTimeout);
  }, [triggerKey, fireBurst]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div className="podium-celebration" ref={containerRef}>
      <canvas ref={canvasRef} className="podium-fireworks-canvas" />
      {children}
    </div>
  );
};

export default PodiumFireworks;
